import { useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { cn } from "@/src/lib/utils";
import { useOffersStore } from "@/src/stores/offersStore";
import { Booking } from "../../model";
import { EditOperatorPricingModal } from "../Operator";
import { buildOperatorPricing } from "../../operatorPricing";

const formatPrice = (price: number, code: string) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: code || "EUR" }).format(price || 0);

/**
 * Operator pricing card, shared by the Overview and Operator sections so the
 * figures and checks can't drift apart. Row markup deliberately mirrors the
 * customer pricing card beside it (same gap, text sizes and label/value layout).
 */
export function OperatorPricingCard({
  booking,
  operatorCurrencyCode,
  tab,
  className,
}: {
  booking: Booking;
  operatorCurrencyCode: string;
  tab: "overview" | "operator";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const { currencies } = useOffersStore();
  const p = buildOperatorPricing(booking, currencies, operatorCurrencyCode);
  const ccy = p.currencyCode;

  return (
    <Card className={cn("flex grow w-[546px] flex-col gap-2", className)}>
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Operator pricing</h3>
        <Button onClick={() => setOpen(true)} variant="link">
          Edit
        </Button>
        <EditOperatorPricingModal
          tab={tab}
          defaultValues={{ clientPrice: p.clientPrice, netCharterFee: p.netCharterFee, extras: p.extras }}
          bookingId={booking.id}
          open={open}
          onOpenChange={setOpen}
          operatorCurrencyCode={ccy}
        />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base">Client charter price</span>
          <span className="text-xs text--gray-500">
            {p.clientPriceSource === "manual"
              ? "set on operator pricing"
              : p.converted
                ? "customer's discounted charter price, converted"
                : "customer's discounted charter price"}
          </span>
        </div>
        <span className="text-base text--slate-900 font-medium">{formatPrice(p.clientPrice, ccy)}</span>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base">Net charter fee</span>
          <span className="text-xs text--gray-500">{p.netCharterFeeSource === "manual" ? "set on operator pricing" : p.netCharterFeeSource === "booking" ? "from the booking's net fee (contract / commission)" : p.netCharterFeeSource === "commission" ? "client charter price less the commission" : "operator price from the offer"}</span>
        </div>
        <span className="text-base text--slate-900 font-medium">{formatPrice(p.netCharterFee, ccy)}</span>
      </div>
      {p.vat > 0 && (
        <div className="flex justify-between items-center">
          <div className="flex flex-col"><span className="text-base">VAT</span><span className="text-xs text--gray-500">passed through to the operator</span></div>
          <span className="text-base text--slate-900 font-medium">{formatPrice(p.vat, ccy)}</span>
        </div>
      )}
      {p.apa > 0 && (
        <div className="flex justify-between items-center">
          <div className="flex flex-col"><span className="text-base">APA</span><span className="text-xs text--gray-500">advance provisioning, passed through to the operator</span></div>
          <span className="text-base text--slate-900 font-medium">{formatPrice(p.apa, ccy)}</span>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base">Extras (in advance)</span>
          {p.advanceExtras.map((am, index) => (
            <span className="text-xs text--gray-500" key={index}>
              {am.text}
            </span>
          ))}
        </div>
        <span className="text-base text--slate-900 font-medium">{formatPrice(p.extras, ccy)}</span>
      </div>
      {p.extrasMismatch != null && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Extras don't match the customer side. The customer pays {formatPrice(p.customerAdvanceExtras, ccy)} in
          advance{p.converted ? " (converted)" : ""}, but the operator side has {formatPrice(p.extras, ccy)}
          {p.extrasMismatch > 0
            ? ` — ${formatPrice(p.extrasMismatch, ccy)} isn't being passed to the operator.`
            : ` — ${formatPrice(-p.extrasMismatch, ccy)} more than the customer pays.`}{" "}
          Use Edit to correct it.
        </p>
      )}
      <div className="flex justify-between items-center">
        <span className="text-base font-semibold">Total operator payable</span>
        <span className="text-base text--slate-900 font-medium">{formatPrice(p.totalPayable, ccy)}</span>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base font-semibold">Commission</span>
          <span className="text-xs text--gray-500">
            client charter price − net charter fee{p.commissionPct != null ? ` · ${p.commissionPct}%` : ""}
          </span>
        </div>
        <span className={cn("text-base font-medium", p.commission < 0 ? "text-red-600" : "text--slate-900")}>
          {formatPrice(p.commission, ccy)}
        </span>
      </div>
    </Card>
  );
}
